/**
 * PATH: apps/quote/src/components/builder/panels/AIAssistantPanel.tsx
 * PURPOSE: AI Assistant panel orchestrator with actionable suggestions
 * WHY: Embeddable content panel for unified right drawer — AI-powered quote assistance
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { API_BASE } from '@quotech/api'
import { Bot, Send, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type AISuggestion } from '@/lib/quoteApi'
import { useToast } from '../Toast'

// Sub-modules
import { QUICK_ACTIONS, type Message, type AIAssistantPanelProps, type BackendAISuggestion, type AISuggestionsResponse } from './ai-assistant/ai-assistant-types'
import AuditResultDisplay from './ai-assistant/AuditResultDisplay'
import SimilarQuotesDisplay from './ai-assistant/SimilarQuotesDisplay'
import CoverageResultDisplay from './ai-assistant/CoverageResultDisplay'
import SuggestionCard from './ai-assistant/SuggestionCard'
import AISuggestionsDisplay from './ai-assistant/AISuggestionsDisplay'

export default function AIAssistantPanel({
  quoteId, quote, onApplySuggestion, onOpenQuote,
}: AIAssistantPanelProps) {
  const [message, setMessage] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [activeAction, setActiveAction] = useState<string | null>(null)
  const [backendSuggestions, setBackendSuggestions] = useState<BackendAISuggestion[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ id: 'welcome', role: 'system', content: "Hi! I'm your AI assistant. Use the quick actions above or ask me about pricing, scope, or missing items.", timestamp: new Date() }])
    }
  }, [])

  const auditMutation = useMutation({
    mutationFn: () => quoteApi.runQuoteAudit(quoteId!),
    onSuccess: (result) => { setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: `Audit complete. Score: ${result.score}/100`, auditResult: result, timestamp: new Date() }]) },
    onError: (err: unknown) => { toast.error('Audit failed: ' + (err?.message || 'Unknown error')) },
    onSettled: () => setActiveAction(null),
  })

  const similarMutation = useMutation({
    mutationFn: () => quoteApi.findSimilarQuotes(quoteId!),
    onSuccess: (result) => { setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: `Found ${result.similar_quotes?.length || 0} similar quotes for reference.`, similarQuotes: result, timestamp: new Date() }]) },
    onError: (err: unknown) => { toast.error('Search failed: ' + (err?.message || 'Unknown error')) },
    onSettled: () => setActiveAction(null),
  })

  const optimizeMutation = useMutation({
    mutationFn: () => quoteApi.optimizePricing(quoteId!),
    onSuccess: (result) => { setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: result.recommendations?.length ? `Found ${result.recommendations.length} pricing optimization suggestions.` : 'No pricing optimizations found. Your quote looks well-priced!', pricingResult: result, timestamp: new Date() }]) },
    onError: (err: unknown) => { toast.error('Optimization failed: ' + (err?.message || 'Unknown error')) },
    onSettled: () => setActiveAction(null),
  })

  const coverageMutation = useMutation({
    mutationFn: () => quoteApi.checkCoverage(quoteId!),
    onSuccess: (result) => { setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: `Coverage analysis complete: ${result.coverage_percent}%`, coverageResult: result, timestamp: new Date() }]) },
    onError: (err: unknown) => { toast.error('Coverage check failed: ' + (err?.message || 'Unknown error')) },
    onSettled: () => setActiveAction(null),
  })

  const generateSuggestionsMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/api/quotebuilder/quotes/${quoteId}/ai-suggestions/generate/`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
      if (!response.ok) throw new Error('Failed to generate suggestions')
      return response.json() as Promise<AISuggestionsResponse>
    },
    onSuccess: (result) => {
      setBackendSuggestions(result.suggestions || [])
      setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: result.total > 0 ? `Generated ${result.total} AI suggestions. ${result.high_confidence} are high confidence.` : 'No suggestions found. Your quote looks good!', timestamp: new Date() }])
    },
    onError: (err: unknown) => { toast.error('Failed to generate suggestions: ' + (err?.message || 'Unknown error')) },
    onSettled: () => setActiveAction(null),
  })

  const acceptSuggestionMutation = useMutation({
    mutationFn: async (suggestionId: string) => {
      const response = await fetch(`${API_BASE}/api/quotebuilder/ai-suggestions/${suggestionId}/accept/`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
      if (!response.ok) throw new Error('Failed to accept suggestion')
      return response.json()
    },
    onSuccess: (_, suggestionId) => {
      setBackendSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, status: 'accepted' as const } : s))
      toast.success('Suggestion applied!')
      queryClient.invalidateQueries({ queryKey: ['quote', quoteId] })
    },
    onError: (err: unknown) => { toast.error('Failed to apply suggestion: ' + (err?.message || 'Unknown error')) },
  })

  const rejectSuggestionMutation = useMutation({
    mutationFn: async (suggestionId: string) => {
      const response = await fetch(`${API_BASE}/api/quotebuilder/ai-suggestions/${suggestionId}/reject/`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } })
      if (!response.ok) throw new Error('Failed to reject suggestion')
      return response.json()
    },
    onSuccess: (_, suggestionId) => { setBackendSuggestions(prev => prev.map(s => s.id === suggestionId ? { ...s, status: 'rejected' as const } : s)) },
  })

  const suggestionMutation = useMutation({
    mutationFn: (query: string) => quoteApi.getAISuggestions(query),
    onSuccess: (data, query) => { setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'assistant', content: data.length ? `Found ${data.length} relevant items for "${query}":` : `No suggestions found for "${query}". Try a different search.`, suggestions: data.length > 0 ? data : undefined, timestamp: new Date() }]) },
    onError: (err: unknown) => { toast.error('Search failed: ' + (err?.message || 'Unknown error')) },
  })

  const handleQuickAction = useCallback((actionId: string) => {
    if (!quoteId) { toast.warning('Please open a quote first'); return }
    setActiveAction(actionId)
    const actionLabels: Record<string, string> = { suggestions: 'Generate AI suggestions for pricing and quantities', audit: 'Run a quote audit', optimize: 'Suggest pricing optimizations', coverage: 'Check scope coverage', similar: 'Find similar quotes' }
    setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'user', content: actionLabels[actionId] || actionId, timestamp: new Date() }])
    switch (actionId) {
      case 'suggestions': generateSuggestionsMutation.mutate(); break
      case 'audit': auditMutation.mutate(); break
      case 'similar': similarMutation.mutate(); break
      case 'optimize': optimizeMutation.mutate(); break
      case 'coverage': coverageMutation.mutate(); break
    }
  }, [quoteId, generateSuggestionsMutation, auditMutation, similarMutation, optimizeMutation, coverageMutation, toast])

  const handleSend = useCallback(() => {
    const query = message.trim()
    if (!query) return
    setMessages((prev) => [...prev, { id: Date.now().toString(), role: 'user', content: query, timestamp: new Date() }])
    setMessage('')
    suggestionMutation.mutate(query)
  }, [message, suggestionMutation])

  const handleApplySuggestion = useCallback((suggestion: AISuggestion) => {
    quoteApi.recordAIFeedback(suggestion.id, 'accepted')
    onApplySuggestion?.(suggestion)
    toast.success(`Added "${suggestion.item_name}" to quote`)
  }, [onApplySuggestion, toast])

  const isLoading = auditMutation.isPending || similarMutation.isPending || optimizeMutation.isPending || coverageMutation.isPending || suggestionMutation.isPending || generateSuggestionsMutation.isPending || acceptSuggestionMutation.isPending || rejectSuggestionMutation.isPending

  return (
    <div className="h-full flex flex-col">
      {/* Quick Actions */}
      <div className="flex-shrink-0 p-3 border-b border-slate-200 dark:border-slate-800">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Quick Actions</p>
        <div className="grid grid-cols-2 gap-2">
          {QUICK_ACTIONS.map((action) => {
            const Icon = action.icon
            const isActive = activeAction === action.id
            return (
              <button key={action.id} onClick={() => handleQuickAction(action.id)} disabled={isLoading}
                className={cn('flex flex-col items-start gap-1 p-2 rounded-lg transition-colors text-left', isActive ? 'bg-blue-100 dark:bg-blue-900/30 ring-1 ring-blue-500' : action.bgColor, 'hover:opacity-80 disabled:opacity-50')}>
                <div className="flex items-center gap-1.5">
                  {isActive ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" /> : <Icon className={cn('w-4 h-4', action.color)} />}
                  <span className="text-xs font-semibold text-slate-900 dark:text-white">{action.label}</span>
                </div>
                <span className="text-[10px] text-slate-500 dark:text-slate-400">{action.description}</span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg) => (
          <div key={msg.id} className={cn('flex', msg.role === 'user' && 'justify-end')}>
            <div className={cn('max-w-[90%] rounded-lg px-3 py-2', msg.role === 'user' ? 'bg-blue-600 text-white' : msg.role === 'system' ? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700')}>
              {msg.role === 'assistant' && (<div className="flex items-center gap-1.5 mb-1.5"><Bot className="w-3.5 h-3.5 text-purple-500" /><span className="text-xs font-medium text-purple-600 dark:text-purple-400">AI Assistant</span></div>)}
              <p className="text-sm">{msg.content}</p>
              {msg.auditResult && <div className="mt-2"><AuditResultDisplay result={msg.auditResult} /></div>}
              {msg.similarQuotes && <div className="mt-2"><SimilarQuotesDisplay result={msg.similarQuotes} onOpenQuote={onOpenQuote} /></div>}
              {msg.coverageResult && <div className="mt-2"><CoverageResultDisplay result={msg.coverageResult} /></div>}
              {msg.suggestions && msg.suggestions.length > 0 && (<div className="mt-2 space-y-2">{msg.suggestions.slice(0, 5).map((s) => <SuggestionCard key={s.id} suggestion={s} onApply={() => handleApplySuggestion(s)} />)}</div>)}
            </div>
          </div>
        ))}
        {backendSuggestions.length > 0 && (<div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-3"><AISuggestionsDisplay suggestions={backendSuggestions} onAccept={(id) => acceptSuggestionMutation.mutate(id)} onReject={(id) => rejectSuggestionMutation.mutate(id)} /></div>)}
        {isLoading && (<div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">{generateSuggestionsMutation.isPending ? 'Analyzing quote for suggestions...' : 'Thinking...'}</span></div>)}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-3 border-t border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder="Ask about pricing, scope..." className="flex-1 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" disabled={isLoading} />
          <button onClick={handleSend} disabled={isLoading || !message.trim()} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  )
}
